import { isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import UnavailabilityBanner from 'app/components/unavailability-banner';
import { Column } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useIsCurrentlyUnavailable } from 'app/hooks/use-is-currently-unavailable';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { useCanUseServices } from 'app/network/category';
import { ChannelType, SUPPORT_CHANNEL_URL, useCreateChannel } from 'app/network/chat/channel';
import { useProfile } from 'app/network/profile';
import { useJoinPlace, useLeavePlace, useMyPlaces, useSharingPlaceMembers } from 'app/network/sharing-place';
import { useStatistics } from 'app/network/statistics';
import { useReportUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import type { NavigationScreen } from 'app/types/navigation';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { getTodayOpeningStatus } from 'app/lib/opening-hours';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useKeyboard } from '@react-native-community/hooks';
import {
  ACTIVITY_SECTOR_FEATURE,
  contactProfile,
  getNextOpenDay,
  getProfileAccessory,
  getProfileNavigationOptions,
  getProfileStats,
  hasPlaceDetails,
  isUserFrequentingPlace,
  isViewedAmbassador,
  isViewingOwnProfile,
  openPlaceItinerary,
  shareNeighbourProfile,
  sharePlaceProfile,
  shouldShowSuperSharer,
  theme,
  useProfileListingsData,
  useProfilePageTitle,
} from './components/shared';
import { ProfileBadge } from './components/profile-badge';
import { ProfileStatsAndReport } from './components/profile-stats-and-report';
import { ProfileBottomSheets } from './components/profile-bottom-sheets';
import { ProfileRatingsSection } from './components/profile-ratings-section';
import { ProfileListings } from './components/profile-listings';
import { PlaceDiscountBanner } from './components/place-discount-banner';
import { PlaceCover } from './components/place-cover';
import { ProfileHero } from './components/profile-hero';
import { ProfileBio } from './components/profile-bio';
import { MemberInfoCard } from './components/member-info-card';
import { PlaceInfo } from './components/place-info';
import { ProfileInsurance } from './components/profile-insurance';

function usePlaceProfileState({
  isLoggedIn,
  isMyself,
  profile,
  t,
  userId,
}: {
  isLoggedIn: boolean;
  isMyself: boolean;
  profile: ReturnType<typeof useProfile>['data'];
  t: ReturnType<typeof useTranslation>['t'];
  userId: string;
}) {
  const candidatePlaceDetails = profile?.placeDetails;
  const isPlace = hasPlaceDetails(candidatePlaceDetails);
  const placeDetails = isPlace ? candidatePlaceDetails : undefined;
  const websiteUrl = placeDetails?.websiteUrl?.trim();
  const hasWebsite = Boolean(websiteUrl);
  const openingStatus = getTodayOpeningStatus(placeDetails?.openingHours);
  const nextOpenDay = getNextOpenDay({ isPlace, openingHours: placeDetails?.openingHours, openingStatus, t });
  const myPlacesQuery = useMyPlaces({ enabled: isLoggedIn && isPlace && !isMyself });
  const isFrequenting = isUserFrequentingPlace({ isPlace, placeUserId: userId, places: myPlacesQuery.data });
  const membersQuery = useSharingPlaceMembers(isPlace ? userId : undefined);
  const placeMembers = membersQuery.data ?? [];
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [showJoinConfirm, setShowJoinConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showPlaceHelp, setShowPlaceHelp] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showPlaceMembers, setShowPlaceMembers] = useState(false);
  const { mutate: joinPlace, isPending: isJoining } = useJoinPlace({
    onSuccess: () => setShowJoinConfirm(false),
  });
  const { mutate: leavePlace, isPending: isLeaving } = useLeavePlace({
    onSuccess: () => setShowLeaveConfirm(false),
  });

  return {
    hasWebsite,
    isFrequenting,
    isJoining,
    isLeaving,
    isPlace,
    joinPlace,
    leavePlace,
    nextOpenDay,
    openingStatus,
    placeDetails,
    placeMembers,
    setShowDiscount,
    setShowJoinConfirm,
    setShowLeaveConfirm,
    setShowOpeningHours,
    setShowPlaceHelp,
    setShowPlaceMembers,
    showDiscount,
    showJoinConfirm,
    showLeaveConfirm,
    showOpeningHours,
    showPlaceHelp,
    showPlaceMembers,
    websiteUrl,
  };
}

function reportProfileUser(
  reportUser: ReturnType<typeof useReportUser>['mutate'],
  userId: string,
  navigation: Parameters<typeof navigateToChat>[0],
) {
  reportUser(userId, {
    onSuccess: () => {
      navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
        i18nKey: 'reportUserPrompt',
        ns: 'neighbour-profile',
      });
    },
  });
}

function useProfileActions({
  authorize,
  navigation,
  profile,
  route,
}: Pick<Parameters<typeof contactProfile>[0], 'authorize' | 'navigation' | 'profile' | 'route'>) {
  const { mutate: reportUser, isPending: isReportingUser } = useReportUser();
  const [showReportConfirmation, setShowReportConfirmation] = useState(false);
  const [isImageViewerModalOpen, setIsImageViewerModalOpen] = useState(false);
  const [isAmbassadorSheetOpen, setIsAmbassadorSheetOpen] = useState(false);
  const {
    error: channelMutationError,
    mutate: mutateChannel,
    isPending: isContactPending,
  } = useCreateChannel(route.params.listingId ? ChannelType.Listing : ChannelType.Member, {
    onSuccess: (channel) => navigateToChat(navigation, channel.channel_url),
  });

  const contactButtonHandler = () => {
    contactProfile({ authorize, mutateChannel, navigation, profile, route });
  };
  const handleReportUser = () => {
    setShowReportConfirmation(false);
    reportProfileUser(reportUser, route.params.userId, navigation);
  };

  return {
    channelMutationError,
    contactButtonHandler,
    handleReportUser,
    isAmbassadorSheetOpen,
    isContactPending,
    isImageViewerModalOpen,
    isReportingUser,
    setIsAmbassadorSheetOpen,
    setIsImageViewerModalOpen,
    setShowReportConfirmation,
    showReportConfirmation,
  };
}

function useProfileMetadata(
  profileQuery: ReturnType<typeof useProfile>,
  statisticsQuery: ReturnType<typeof useStatistics>,
) {
  useRegisterLoading(profileQuery.isPending, 'neighbour-profile-profile');
  useRegisterLoading(statisticsQuery.isPending, 'neighbour-profile-statistics');

  const ratings = profileQuery.data?.ratings ?? [];
  return {
    locationLabel: profileQuery.data?.city?.trim() || '-',
    ratedSubjectName: [profileQuery.data?.firstName, profileQuery.data?.lastName].filter(Boolean).join(' ').trim(),
    ratings,
  };
}

function ProfileStatusBadge({
  showSuperPartageur,
  viewedProfileIsAmbassador,
}: {
  showSuperPartageur: boolean;
  viewedProfileIsAmbassador: boolean;
}) {
  return <ProfileBadge showSuperPartageur={showSuperPartageur} viewedProfileIsAmbassador={viewedProfileIsAmbassador} />;
}

const NeighbourProfileScreenComponent: NavigationScreen<'neighbour-profile'> = ({ navigation, route }) => {
  const { t } = useTranslation();
  const userInfo = useAtomValue(userInfoAtom);
  const { isEnabled: ambassadorEnabled } = useAmbassador();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isMyself = isViewingOwnProfile(userInfo?.id, route.params.userId);
  const ns = isMyself ? 'my-profile' : 'neighbour-profile';
  const statisticsQuery = useStatistics(route.params.userId);
  const canUseServices = useCanUseServices();
  const canUseDonations = Boolean(userInfo?.canPublishDonations);
  const canUseSales = isFeatureEnabled(FeatureFlag.showSalesOnPublicProfile, userInfo?.featureFlags);
  const { isSmallUI, isLargeUI } = useUISize();
  const canPublishListings = statisticsQuery.data?.canPublish;
  const sharingPlacesEnabled = isFeatureEnabled(FeatureFlag.sharingPlaces, userInfo?.featureFlags);
  const profileQuery = useProfile(route.params.userId, {
    throwOnError: true,
  });
  const insets = useSafeAreaInsets();
  const horizontalPadding = insets.left || theme.spacing['spacing.general.x4'];
  const keyboard = useKeyboard();
  const place = usePlaceProfileState({
    isLoggedIn,
    isMyself,
    profile: profileQuery.data,
    t,
    userId: route.params.userId,
  });
  const {
    hasWebsite,
    isFrequenting,
    isJoining,
    isLeaving,
    isPlace,
    joinPlace,
    leavePlace,
    nextOpenDay,
    openingStatus,
    placeDetails,
    placeMembers,
    setShowDiscount,
    setShowJoinConfirm,
    setShowLeaveConfirm,
    setShowOpeningHours,
    setShowPlaceHelp,
    setShowPlaceMembers,
    showDiscount,
    showJoinConfirm,
    showLeaveConfirm,
    showOpeningHours,
    showPlaceHelp,
    showPlaceMembers,
    websiteUrl,
  } = place;
  const listings = useProfileListingsData({
    isLoggedIn,
    isMyself,
    isPlace,
    isSmallUI,
    userId: route.params.userId,
  });
  useNavigationOptions(
    navigation,
    getProfileNavigationOptions({
      isLargeUI,
      isPlace,
      isRoot: navigation.getState().index === 0,
      ns,
      onHelp: () => setShowPlaceHelp(true),
      t,
    }),
  );

  const openItinerary = () => openPlaceItinerary(placeDetails);
  const sharePlace = () => sharePlaceProfile(websiteUrl, profileQuery.data);
  const shareProfile = () => shareNeighbourProfile(profileQuery.data, t);
  const isUnavailable = useIsCurrentlyUnavailable(profileQuery.data?.unavailability);
  useProfilePageTitle({ isPlace, ns, profile: profileQuery.data, t });

  const authorize = useIsExpired(true);
  const {
    channelMutationError,
    contactButtonHandler,
    handleReportUser,
    isAmbassadorSheetOpen,
    isContactPending,
    isImageViewerModalOpen,
    isReportingUser,
    setIsAmbassadorSheetOpen,
    setIsImageViewerModalOpen,
    setShowReportConfirmation,
    showReportConfirmation,
  } = useProfileActions({ authorize, navigation, profile: profileQuery.data, route });
  const showSuperPartageur = shouldShowSuperSharer(profileQuery.data);
  const viewedProfileIsAmbassador = isViewedAmbassador(ambassadorEnabled, profileQuery.data?.ambassadorStatus);
  const badge = <ProfileStatusBadge {...{ showSuperPartageur, viewedProfileIsAmbassador }} />;

  const canRateAfterLoans = isFeatureEnabled(FeatureFlag.rateAfterLoans, userInfo?.featureFlags);

  const emojiStatsElements = getProfileStats({
    canPublishListings,
    canRateAfterLoans,
    canUseDonations,
    canUseSales,
    canUseServices,
    statistics: statisticsQuery.data,
    t,
  });

  const { locationLabel, ratedSubjectName, ratings } = useProfileMetadata(profileQuery, statisticsQuery);

  const defaultPaddingStyle = usePagePadding();

  const paddingHorizontal = defaultPaddingStyle.paddingLeft;

  const ratingsSection = (
    <ProfileRatingsSection
      currentUserId={userInfo?.id}
      isMyself={isMyself}
      navigation={navigation}
      ratedUserId={route.params.userId}
      ratings={ratings}
      subjectName={ratedSubjectName}
    />
  );

  return (
    <>
      <Page
        showsVerticalScrollIndicator={false}
        onScroll={listings.pagination.handlePageScroll}
        onScrollBeginDrag={listings.pagination.handlePageScrollBeginDrag}
        scrollEventThrottle={16}
        accessory={getProfileAccessory({
          canReceiveInAppContact: placeDetails?.allowInAppContact !== false,
          channelMutationError,
          contactButtonHandler,
          isContactPending,
          isMyself,
          isPlace,
          keyboardShown: keyboard.keyboardShown,
          ns,
          onShareProfile: () => void shareProfile(),
          paddingHorizontal,
          t,
        })}

        paddingStyle={{
          ...defaultPaddingStyle,
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: theme.spacing['spacing.general.x12'],
        }}
      >
        {isUnavailable ? <UnavailabilityBanner /> : null}
        <Column>
          <PlaceDiscountBanner onOpen={() => setShowDiscount(true)} placeDetails={placeDetails} />
          <PlaceCover placeDetails={placeDetails} />

          <Column
            gap="spacing.general.x2"
            style={{
              width: '100%',
              paddingHorizontal: theme.spacing['spacing.general.x5'],
              paddingTop: theme.spacing['spacing.general.x2'],
              borderRadius: theme.borderRadii['radius.lg'],
              borderColor: theme.colors['surface.disabled'],
              backgroundColor: theme.colors['surface.default'],
            }}
          >
            <ProfileHero
              badge={badge}
              isImageViewerModalOpen={isImageViewerModalOpen}
              isUnavailable={isUnavailable}
              onCloseImage={() => setIsImageViewerModalOpen(false)}
              onOpenAmbassador={() => setIsAmbassadorSheetOpen(true)}
              onOpenImage={() => setIsImageViewerModalOpen(true)}
              profile={profileQuery.data}
              showSuperPartageur={showSuperPartageur}
              trustPoints={statisticsQuery.data?.loans.trustPoints}
              viewedProfileIsAmbassador={viewedProfileIsAmbassador}
            />
            <ProfileBio profile={profileQuery.data} />
            <MemberInfoCard
              activitySectorsEnabled={isFeatureEnabled(ACTIVITY_SECTOR_FEATURE, userInfo?.featureFlags)}
              isPlace={isPlace}
              locationLabel={locationLabel}
              ns={ns}
              profile={profileQuery.data}
              statistics={statisticsQuery.data}
            />
            <PlaceInfo
              horizontalPadding={horizontalPadding}
              members={placeMembers}
              nextOpenDay={nextOpenDay}
              onFrequentingPress={() => (isFrequenting ? setShowLeaveConfirm(true) : setShowJoinConfirm(true))}
              onHelp={() => setShowPlaceHelp(true)}
              onItinerary={openItinerary}
              onOpenMembers={() => setShowPlaceMembers(true)}
              onOpenOpeningHours={() => setShowOpeningHours(true)}
              onShare={() => void sharePlace()}
              openingStatus={openingStatus}
              placeDetails={isPlace ? placeDetails : null}
              variant={{
                membership: isFrequenting ? 'frequenting' : 'not-frequenting',
                membershipAction: isJoining || isLeaving ? 'pending' : 'idle',
                viewer: isMyself ? 'self' : 'visitor',
                website: hasWebsite ? 'available' : 'unavailable',
              }}
              websiteUrl={websiteUrl}
            />
            <ProfileInsurance isPlace={isPlace} profile={profileQuery.data} />

            <ProfileStatsAndReport
              emojiStatsElements={emojiStatsElements}
              isMyself={isMyself}
              isPlace={isPlace}
              isReportingUser={isReportingUser}
              ns={ns}
              onReport={() => setShowReportConfirmation(true)}
            />
          </Column>
          <View
            style={{
              height: theme.spacing['spacing.general.x5'],
              marginTop: theme.spacing['spacing.general.x5'],
              borderTopWidth: 1,
              borderBottomWidth: 1,
              borderColor: theme.colors['surface.disabled'],
              backgroundColor: theme.colors['surface.tertiary-neutral'],
            }}
          />

          <ProfileListings
            isPlace={isPlace}
            place={{
              available: listings.placeAvailable,
              onTabChange: listings.setPlaceTab,
              ratings: ratingsSection,
              regulars: listings.placeRegulars,
              searchQuery: listings.searchQuery,
              setSearchQuery: listings.setSearchQuery,
              tab: listings.placeTab,
            }}
            neighbour={{
              frequentedPlaces: profileQuery.data?.frequentedPlaces,
              isLargeUI,
              ns,
              offers: listings.offers,
              onTabChange: listings.setNeighbourTab,
              ratings: ratingsSection,
              requests: listings.requests,
              searchQuery: listings.searchQuery,
              setSearchQuery: listings.setSearchQuery,
              sharingPlacesEnabled,
              tab: listings.neighbourTab,
            }}
          />
        </Column>
      </Page>

      <ProfileBottomSheets
        ambassador={{ onClose: () => setIsAmbassadorSheetOpen(false), open: isAmbassadorSheetOpen }}
        place={
          placeDetails
            ? {
                details: placeDetails,
                discount: { onClose: () => setShowDiscount(false), open: showDiscount },
                help: { onClose: () => setShowPlaceHelp(false), open: showPlaceHelp },
                join: {
                  isJoining,
                  onClose: () => setShowJoinConfirm(false),
                  onJoin: () => joinPlace(route.params.userId),
                  open: showJoinConfirm,
                },
                leave: {
                  isLeaving,
                  onClose: () => setShowLeaveConfirm(false),
                  onLeave: () => leavePlace(route.params.userId),
                  open: showLeaveConfirm,
                },
                members: placeMembers,
                membersSheet: { onClose: () => setShowPlaceMembers(false), open: showPlaceMembers },
                navigation,
                openingHours: { onClose: () => setShowOpeningHours(false), open: showOpeningHours },
              }
            : undefined
        }
        report={{
          namespace: ns,
          onClose: () => setShowReportConfirmation(false),
          onConfirm: handleReportUser,
          open: showReportConfirmation,
        }}
      />
    </>
  );
};

export const NeighbourProfileScreen: NavigationScreen<'neighbour-profile'> = withOptionalMetrics(
  'NeighbourProfileScreen',
  withErrorBoundary(NeighbourProfileScreenComponent),
);
